import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import EmblaCarousel, { EmblaCarouselType } from 'embla-carousel'
import AutoScroll, { AutoScrollType } from 'embla-carousel-auto-scroll'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

export class PressMarquee extends UcoastEl {
	static htmlSelector = 'press-marquee'
	private viewport: HTMLElement
	private header: HTMLElement
	private toggle?: HTMLButtonElement
	private speed: number
	private emblaApi?: EmblaCarouselType
	private autoScroll?: AutoScrollType
	private observer?: IntersectionObserver
	private motionPreference = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	)
	private userPaused = false
	private inView = false
	private hovering = false
	private focused = false
	private interacting = false
	private moved = false
	private handleMotionChange = () => this.sync()

	constructor() {
		super()
		this.viewport = q.rs('[data-uc-press-marquee-viewport]', this)
		this.header = q.rs('[data-uc-press-marquee-header]', this)
		this.toggle = q.os<HTMLButtonElement>(
			'[data-uc-press-marquee-toggle]',
			this
		)
		this.speed = Number(this.dataset.speed) / 10 || 0.2

		this.handleFocus = this.handleFocus.bind(this)

		this.toggle?.addEventListener('click', () => {
			this.userPaused = !this.userPaused
			this.updateToggle()
			this.sync()
		})
		this.viewport.addEventListener('pointerenter', (event) => {
			if (event.pointerType !== 'mouse') return
			this.hovering = true
			this.sync()
		})
		this.viewport.addEventListener('pointerleave', (event) => {
			if (event.pointerType !== 'mouse') return
			this.hovering = false
			this.sync()
		})
		this.addEventListener('focusin', this.handleFocus)
		this.addEventListener('focusout', this.handleFocus)
	}

	override connectedCallback(): void {
		super.connectedCallback()
		this.initCarousel()
		this.motionPreference.addEventListener(
			'change',
			this.handleMotionChange
		)
		this.observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				this.inView = entry.isIntersecting
			}
			this.sync()
		})
		this.observer.observe(this)
	}

	private initCarousel(): void {
		// The static inset moves from CSS padding to Embla's align so the loop seam keeps a single gap
		this.setAttribute('data-uc-ready', '')
		this.autoScroll = AutoScroll({
			speed: this.speed,
			playOnInit: false,
			stopOnInteraction: true,
			stopOnFocusIn: false,
		})
		this.emblaApi = EmblaCarousel(
			this.viewport,
			{
				loop: true,
				dragFree: true,
				align: () =>
					parseFloat(getComputedStyle(this.header).paddingLeft) || 0,
			},
			[this.autoScroll, WheelGesturesPlugin()]
		)

		this.emblaApi
			.on('pointerDown', () => {
				this.interacting = true
				this.moved = false
				this.viewport.setAttribute('data-uc-dragging', '')
				this.sync()
			})
			.on('scroll', () => {
				if (this.interacting) this.moved = true
			})
			// A press that never moved the rail starts no glide, so no settle event follows
			.on('pointerUp', () => {
				this.viewport.removeAttribute('data-uc-dragging')
				if (this.moved) return
				this.interacting = false
				this.sync()
			})
			// Embla settles about 2s after a glide looks stopped, which is the rest we want before resuming
			.on('settle', () => {
				if (!this.interacting) return
				this.interacting = false
				this.sync()
			})
			// Resizing re-initialises plugins, which drops auto-scroll back to stopped
			.on('reInit', () => this.sync())
	}

	private sync(): void {
		const autoScroll = this.autoScroll
		if (!autoScroll) return

		const shouldPlay =
			this.inView &&
			!this.userPaused &&
			!this.hovering &&
			!this.focused &&
			!this.interacting &&
			!this.motionPreference.matches

		if (!shouldPlay) {
			autoScroll.stop()
			return
		}

		if (!autoScroll.isPlaying()) autoScroll.play(0)
	}

	// Only keyboard focus pauses; a mouse drag also focuses the card link it started on
	private handleFocus(event: FocusEvent): void {
		if (event.type === 'focusin') {
			const target = q.ot<FocusEvent>(event)
			this.focused =
				target instanceof HTMLElement &&
				this.viewport.contains(target) &&
				target.matches(':focus-visible')
		} else if (
			!(
				event.relatedTarget instanceof Node &&
				this.viewport.contains(event.relatedTarget)
			)
		) {
			this.focused = false
		}
		this.sync()
	}

	private updateToggle(): void {
		if (!this.toggle) return
		const label = this.userPaused
			? this.toggle.dataset.playLabel
			: this.toggle.dataset.pauseLabel

		this.toggle.dataset.state = this.userPaused ? 'paused' : 'playing'
		if (label) this.toggle.setAttribute('aria-label', label)
	}

	override disconnectedCallback(): void {
		this.observer?.disconnect()
		this.motionPreference.removeEventListener(
			'change',
			this.handleMotionChange
		)
		this.emblaApi?.destroy()
		this.emblaApi = undefined
		this.autoScroll = undefined
		this.removeAttribute('data-uc-ready')
		super.disconnectedCallback()
	}
}
